import { format } from 'date-fns'
import * as XLSX from 'xlsx'

import { MarketTypes } from '../constants'
import { OddsFilters } from '../types'
import { OddsDataRow } from './oddsData.dde.table'

type GenerateCsvParams = {
  tableRows: OddsDataRow[]
  filters: OddsFilters
  tournamentName: string
  playerNames?: string[]
  marketType: MarketTypes
  includeModelled: boolean
  selectedPlayerNames?: string[]
}

export const generateOddsDataCsv = ({
  tableRows,
  filters,
  tournamentName,
  playerNames,
  marketType,
  includeModelled,
  selectedPlayerNames
}: GenerateCsvParams) => {
  const startTime = filters.startDateTime
    ? format(filters.startDateTime, 'yyyy-MM-dd_HH-mm-ss')
    : 'Unknown'
  const endTime = filters.endDateTime
    ? format(filters.endDateTime, 'yyyy-MM-dd_HH-mm-ss')
    : 'Unknown'
  const marketTypeName = marketType === MarketTypes.WINNER ? 'Winner' : 'ThreeBall'
  const sanitizedTournamentName = tournamentName.replace(/[^a-zA-Z0-9]/g, '_')
  const fileName = `${sanitizedTournamentName}_${startTime}_${endTime}_${marketTypeName}.xlsx`

  const workbook = XLSX.utils.book_new()

  const oddsDataHeaders: string[] = ['Action Type', 'Player', 'Time']

  if (marketType === MarketTypes.WINNER) {
    oddsDataHeaders.push('DL Trading Odds')
    if (includeModelled) {
      oddsDataHeaders.push('Modelled Odds')
      oddsDataHeaders.push('Difference %')
    }
  } else if (marketType === MarketTypes.THREE_BALL && playerNames) {
    playerNames.forEach((playerName) => {
      oddsDataHeaders.push(`${playerName} - DL Trading Odds`)
      if (includeModelled) {
        oddsDataHeaders.push(`${playerName} - Modelled Odds`)
      }
    })
    if (includeModelled) {
      oddsDataHeaders.push('Difference %')
    }
  }

  const oddsDataRows = tableRows.map((row) => {
    const formattedTime = row.time ? format(new Date(row.time), 'HH:mm:ss') : row.time
    const rowData: (string | number)[] = [row.actionType, row.player, formattedTime]

    if (marketType === MarketTypes.WINNER) {
      rowData.push(row.odds.dlTradingOdds)
      if (includeModelled) {
        rowData.push(row.odds.modelledOdds)
        rowData.push(row.difference !== undefined ? `${row.difference.toFixed(1)}%` : '-')
      }
    } else if (marketType === MarketTypes.THREE_BALL && row.players) {
      row.players.forEach((playerOdds) => {
        rowData.push(playerOdds.dlTradingOdds)
        if (includeModelled) {
          rowData.push(playerOdds.modelledOdds)
        }
      })
      if (includeModelled) {
        rowData.push(row.difference !== undefined ? `${row.difference.toFixed(1)}%` : '-')
      }
    }

    return rowData
  })

  const oddsDataSheet = XLSX.utils.aoa_to_sheet([oddsDataHeaders, ...oddsDataRows])
  XLSX.utils.book_append_sheet(workbook, oddsDataSheet, 'Odds Data')

  const playersValue =
    selectedPlayerNames && selectedPlayerNames.length > 0
      ? selectedPlayerNames.join(', ')
      : filters.players.length > 0
        ? filters.players.join(', ')
        : ''

  const actionTypeValue =
    filters.actionType.length > 0
      ? filters.actionType.map((option) => option.label).join(', ')
      : 'All Actions'

  const filtersData = [
    ['Filter', 'Value'],
    ['Market Type', filters.marketType?.label || ''],
    ['Action Type', actionTypeValue],
    [
      'Start Date/Time',
      filters.startDateTime ? format(filters.startDateTime, 'yyyy-MM-dd HH:mm:ss') : ''
    ],
    [
      'End Date/Time',
      filters.endDateTime ? format(filters.endDateTime, 'yyyy-MM-dd HH:mm:ss') : ''
    ],
    ['Players', playersValue]
  ]

  const filtersSheet = XLSX.utils.aoa_to_sheet(filtersData)
  XLSX.utils.book_append_sheet(workbook, filtersSheet, 'Filters')

  XLSX.writeFile(workbook, fileName)
}
